import React, {
  FunctionComponent,
  ComponentProps,
  useCallback,
  useState,
  useEffect
} from 'react';
import 'leaflet/dist/leaflet.css';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { MapController, MapHandle } from './MapController/MapController';
import { classes } from '../../utils/utils';
import styles from './BasemapMap.module.css';
import { useEventCallback } from '../../hooks/useEventCallback';

export type BasemapMapProps = PropsWithoutChildren<
  Omit<ComponentProps<'div'>, 'ref'>
> & {
  refHandle?: (handle: MapHandle | null) => void;
  onMapMove?: (handle: MapHandle) => void;
};

// eslint-disable-next-line react/no-multi-comp
export const BasemapMap: FunctionComponent<BasemapMapProps> = React.memo(
  (props) => {
    const { refHandle, onMapMove, className, ...remainingProps } = prepareProps(
      { props, ignoreChildren: true }
    );

    const [mapController, setMapController] = useState<MapController | null>(
      null
    );

    const handleMapMove = useEventCallback((handle) => {
      onMapMove?.(handle);
    });

    const containerRef = useCallback(
      (element: HTMLDivElement | null) => {
        setMapController((oldMapController) => {
          oldMapController?.destroy();

          if (element) {
            const controller = new MapController({
              element,
              onMapMove: handleMapMove
            });
            controller.init();
            return controller;
          }

          return null;
        });
      },
      [handleMapMove]
    );

    useEffect(() => {
      refHandle?.(mapController?.getHandle() ?? null);

      return () => {
        refHandle?.(null);
      };
    }, [refHandle, mapController]);

    return (
      <div
        ref={containerRef}
        className={classes(className, styles.container)}
        {...remainingProps}
      ></div>
    );
  }
);
